import { useApolloClient } from '@apollo/client';
// Legacy exception, not the preferred pattern: preserve this row's edit-button sizing
// until its layout is checked with the internal IconButton. Do not copy this import.
// nosemgrep: no-mui-icon-button
import { IconButton } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { LoadingAdornment } from '@/components/form/baseInputs/SelectInput/LoadingAdornment';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { useFormContext } from '@/components/react-hook-form';
import { PrimaryCellTypography } from '@/components/tables/DataTable/components/cells';
import { StyledTableCell } from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import {
  getInferNameMatchCandidatesFromOptions,
  inferNameMatch,
} from '@/modules/assetProviderIntegrations/shared/integrationInference';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';

import { LiabilitiesStepFormShape } from './LiabilitiesStep.types';

interface LiabilitiesStepRowProps {
  liabilityId: string;
  name: string;
  outstandingBalance: string | null;
  issuerAndRecipientOptions: TypeaheadSelectInputOption<string>[];
  onEdit: (liabilityId: string) => void;
  importTableLayout?: boolean;
  sourceName?: string;
  integrationRecipientName?: string | null;
  /**
   * When true, the row's details are only editable once it's checked for
   * import — the fields describe how to import the row, so they mean nothing
   * until it is. Off by default to preserve the legacy trowser's behavior.
   */
  disableUntilSelected?: boolean;
}

export function LiabilitiesStepRow({
  liabilityId,
  name,
  outstandingBalance,
  issuerAndRecipientOptions,
  onEdit,
  importTableLayout = false,
  sourceName = name,
  integrationRecipientName,
  disableUntilSelected = false,
}: LiabilitiesStepRowProps) {
  const apolloClient = useApolloClient();
  const { control, getValues, setValue } =
    useFormContext<LiabilitiesStepFormShape>();
  const [isInferringRecipient, setIsInferringRecipient] = useState(false);
  const inferenceIdRef = useRef(0);
  const hasAttemptedRecipientInferenceRef = useRef(false);

  const shouldImport = useWatch({
    control,
    name: `liabilities.${liabilityId}.shouldImport`,
  });
  const areDetailsDisabled = disableUntilSelected && !shouldImport;
  const displayedName = importTableLayout ? sourceName : name;
  const nameMatchCandidates = useMemo(
    () => getInferNameMatchCandidatesFromOptions(issuerAndRecipientOptions),
    [issuerAndRecipientOptions]
  );

  useEffect(() => {
    const issuerPath = `liabilities.${liabilityId}.issuerId` as const;
    if (importTableLayout && shouldImport && getValues(issuerPath) === '') {
      setValue(issuerPath, NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL);
    }
  }, [getValues, importTableLayout, liabilityId, setValue, shouldImport]);

  useEffect(() => {
    const recipientPath = `liabilities.${liabilityId}.recipientId` as const;
    const shouldImportPath = `liabilities.${liabilityId}.shouldImport` as const;

    if (
      !importTableLayout ||
      !shouldImport ||
      hasAttemptedRecipientInferenceRef.current ||
      getValues(recipientPath) !== NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
    ) {
      return;
    }

    if (!integrationRecipientName?.trim() || nameMatchCandidates.length === 0) {
      return;
    }

    hasAttemptedRecipientInferenceRef.current = true;
    setValue(recipientPath, '');
    const inferenceId = ++inferenceIdRef.current;

    const inferRecipient = async () => {
      queueMicrotask(() => {
        if (inferenceIdRef.current === inferenceId) {
          setIsInferringRecipient(true);
        }
      });
      try {
        const recipientId = await inferNameMatch({
          client: apolloClient,
          name: integrationRecipientName,
          candidates: nameMatchCandidates,
        }).catch(() => null);

        if (
          inferenceIdRef.current !== inferenceId ||
          !getValues(shouldImportPath) ||
          getValues(recipientPath) !== ''
        ) {
          return;
        }

        setValue(
          recipientPath,
          recipientId ?? NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
        );
      } finally {
        if (inferenceIdRef.current === inferenceId) {
          setIsInferringRecipient(false);
        }
      }
    };

    void inferRecipient();
  }, [
    apolloClient,
    getValues,
    importTableLayout,
    integrationRecipientName,
    liabilityId,
    nameMatchCandidates,
    setValue,
    shouldImport,
  ]);

  useEffect(() => {
    if (!shouldImport) {
      inferenceIdRef.current += 1;
      setIsInferringRecipient(false);
      const recipientPath = `liabilities.${liabilityId}.recipientId` as const;
      if (getValues(recipientPath) === '') {
        hasAttemptedRecipientInferenceRef.current = false;
        setValue(recipientPath, NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL);
      }
    }
  }, [getValues, liabilityId, setValue, shouldImport]);

  useEffect(() => {
    return () => {
      inferenceIdRef.current += 1;
      const recipientPath = `liabilities.${liabilityId}.recipientId` as const;
      if (getValues(recipientPath) === '') {
        setValue(recipientPath, NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL);
      }
    };
  }, [getValues, liabilityId, setValue]);

  return (
    <StyledTableRow>
      <StyledTableCell width={40} align="center">
        <FormAwareCheckbox<LiabilitiesStepFormShape>
          control={control}
          fieldName={`liabilities.${liabilityId}.shouldImport`}
          hideLabel
          // Alone in its column, so the row's name is the only thing that can
          // give it an accessible name.
          ariaLabel={`Import ${name}`}
        />
      </StyledTableCell>

      <StyledTableCell>
        <PrimaryCellTypography noWrap title={displayedName}>
          {displayedName}
        </PrimaryCellTypography>
      </StyledTableCell>

      <StyledTableCell>
        <FormAwareTypeaheadSelectInput<LiabilitiesStepFormShape>
          control={control}
          fieldName={`liabilities.${liabilityId}.issuerId`}
          label="Lender"
          hideLabel
          required={shouldImport}
          options={issuerAndRecipientOptions}
          groupBy={(option) => option.groupName ?? ''}
          disabled={areDetailsDisabled}
        />
      </StyledTableCell>

      <StyledTableCell>
        <FormAwareTypeaheadSelectInput<LiabilitiesStepFormShape>
          control={control}
          fieldName={`liabilities.${liabilityId}.recipientId`}
          label="Debtor"
          hideLabel
          required={shouldImport}
          options={issuerAndRecipientOptions}
          groupBy={(option) => option.groupName ?? ''}
          disabled={areDetailsDisabled}
          startAdornment={
            isInferringRecipient ? <LoadingAdornment /> : undefined
          }
        />
      </StyledTableCell>

      {importTableLayout && (
        <StyledTableCell align="right">
          <PrimaryCellTypography>
            {outstandingBalance ?? '—'}
          </PrimaryCellTypography>
        </StyledTableCell>
      )}

      {importTableLayout && (
        <StyledTableCell>
          <PrimaryCellTypography>Other linked fields</PrimaryCellTypography>
        </StyledTableCell>
      )}

      {!importTableLayout && (
        <StyledTableCell align="right">
          <PrimaryCellTypography>
            {outstandingBalance ?? '—'}
          </PrimaryCellTypography>
        </StyledTableCell>
      )}

      <StyledTableCell align="center" width={48}>
        <IconButton
          size="small"
          onClick={() => onEdit(liabilityId)}
          disabled={areDetailsDisabled}
        >
          <Edit02Icon />
        </IconButton>
      </StyledTableCell>
    </StyledTableRow>
  );
}
